import React, { useEffect, useState } from 'react';
import { useChat } from '../../context/ChatContext';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import ConversationList from './ConversationList';
import Loading from '../common/Loading';
import CodeBrowserModal from './CodeBrowserModal';
import FolderUploadModal from './FolderUploadModal';

const ChatInterface = () => {
  const {
    // conversations,
    currentConversation,
    loading,
    error,
    loadConversation,
    sendMessage,
    fetchConversations
  } = useChat();
  
  const [isCodeBrowserOpen, setIsCodeBrowserOpen] = useState(false);
  const [isFolderUploadOpen, setIsFolderUploadOpen] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState(null);

  // Fetch conversations on component mount, but don't auto-create
  useEffect(() => {
    const initializeChat = async () => {
      const convs = await fetchConversations();
      
      // If there are existing conversations but none selected, 
      // load the most recent one
      if (convs && convs.length > 0 && !currentConversation) {
        loadConversation(convs[0].id);
      }
    };
  
    initializeChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We only want this to run once on mount

  const handleSendMessage = async (content) => {
    if (currentConversation) {
      await sendMessage(content);
      
      // Reset selected repo after sending an analysis command
      if (content.startsWith('/analyze-repo') || content.startsWith('/analyze-file')) {
        setSelectedRepo(null);
      }
    }
  };

  const handleSelectConversation = (conversationId) => {
    loadConversation(conversationId);
  };
  
  const handleOpenCodeBrowser = () => {
    setIsCodeBrowserOpen(true);
  };
  
  const handleCloseCodeBrowser = () => {
    setIsCodeBrowserOpen(false);
  };
  
  const handleOpenFolderUpload = () => {
    setIsFolderUploadOpen(true);
  };
  
  const handleCloseFolderUpload = () => {
    setIsFolderUploadOpen(false);
  };
  
  const handleSelectRepo = (repo) => {
    setSelectedRepo(repo);
    setIsCodeBrowserOpen(false);
  };

  return (
    <div className="flex h-full">
      {/* Sidebar with conversation list */}
      <div className="w-64 border-r bg-white h-full">
        <ConversationList
          onSelectConversation={handleSelectConversation}
          currentId={currentConversation?.id}
        />
      </div>

      {/* Main chat area */}
      <div className="flex-1 flex flex-col h-full">
        {error ? (
          <div className="p-4 m-4 text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        ) : !currentConversation ? (
          <div className="flex-1 flex items-center justify-center flex-col">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-medium text-gray-800 mb-2">Welcome to your Personal Chatbot</h2>
              <p className="text-gray-600">
                Select an existing conversation or create a new one to get started.
              </p>
            </div>
            {loading && <Loading text="Loading conversations..." />}
          </div>
        ) : (
          <>
            {/* Conversation header */}
            <div className="p-4 border-b bg-white flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                {currentConversation.title}
              </h2>
              
              <div className="flex space-x-3">
                <button
                  onClick={handleOpenFolderUpload}
                  className="text-gray-600 hover:text-gray-900 flex items-center"
                  title="Upload folder to workspace"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4zm7 5a1 1 0 10-2 0v1.586l-.293-.293a1 1 0 10-1.414 1.414l2 2 .002.002a.997.997 0 001.41 0l.002-.002 2-2a1 1 0 00-1.414-1.414l-.293.293V9z" clipRule="evenodd" />
                  </svg>
                  Upload Folder
                </button>
                
                <button
                  onClick={handleOpenCodeBrowser}
                  className="text-gray-600 hover:text-gray-900 flex items-center"
                  title="Browse code repository"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
                  </svg>
                  Code Browser
                </button>
              </div>
            </div>

            {/* Messages */}
            <MessageList
              messages={currentConversation?.messages || []}
              loading={loading}
            />

            {/* Message input */}
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={loading}
              selectedRepo={selectedRepo}
            />
            
            {/* Code Browser Modal */}
            {isCodeBrowserOpen && (
              <CodeBrowserModal
                isOpen={isCodeBrowserOpen}
                onClose={handleCloseCodeBrowser}
                onSelectRepo={handleSelectRepo}
              />
            )}
            
            {/* Folder Upload Modal */}
            {isFolderUploadOpen && (
              <FolderUploadModal
                isOpen={isFolderUploadOpen}
                onClose={handleCloseFolderUpload}
                conversationId={currentConversation.id}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ChatInterface;